import { Link, useLocation } from 'react-router-dom';
import { useRef, useState, useEffect, useCallback } from 'react';
import {
  Utils,
  useTitle,
  Checkbox,
  useAlert,
  AuthLayout,
  InputField,
  FlexLayout,
  ButtonTypes,
  usePageView,
  StandardButton,
} from '@gv/triage-components';

import { useAppDispatch } from 'store';
import { login } from 'store/slices/auth';
import { Routes, Analytics } from 'config';
import { getDeviceInfo } from 'utils/device';

import { useAuthData } from '../config';

import * as Styles from './styles';
import { parseQuery } from './helpers';
import { FormValues, validationSchema, getInitialValues } from './config';

const { rememberEmail, getRememberedEmail } = Utils.Storage;

const LoginPage = (): JSX.Element => {
  const m1ErrorHandled = useRef(false);
  const [isRememberMe, setRememberMe] = useState(getRememberedEmail() !== null);

  const { image } = useAuthData();

  const location = useLocation();
  const dispatch = useAppDispatch();
  const { handleError } = useAlert();

  usePageView(Analytics.Login);
  useTitle('Login');

  useEffect(() => {
    const { error, googleAuthCode } = parseQuery(location.search);

    if (typeof googleAuthCode === 'string') {
      dispatch(login({ googleAuthCode }))
        .unwrap()
        .then(() => {})
        .catch((err) => handleError(err));
    }

    if (error) {
      handleError(error);
    }
  }, [location.search, handleError]);

  const onSubmit = useCallback(async (values: FormValues) => {
    try {
      localStorage.setItem('hospitalId', '0');
      const deviceInfo = await getDeviceInfo();
      if (!deviceInfo) {
        throw new Error('Failed to get device information');
      }

      const payload = {
        ...deviceInfo,
        password: values.password,
        email: values.email.toLowerCase().trim(),
      };
      await dispatch(login(payload)).unwrap();
      rememberEmail(isRememberMe, values.email);
    } catch (error) {
      const err = error as Error;

      // TODO: Fix M1 error when have a time
      if (
        err.message === 'Maximum call stack size exceeded' &&
        !m1ErrorHandled.current
      ) {
        m1ErrorHandled.current = true;
        onSubmit(values);
        return;
      }

      handleError(error);
    }
  }, []);

  return (
    <AuthLayout<FormValues>
      title="Login"
      onSubmit={onSubmit}
      backgroundImage={image}
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
    >
      {({ dirty, isSubmitting }) => (
        <Styles.FormContainer>
          <FlexLayout gap={24} flexDirection="column">
            <InputField
              required
              type="email"
              name="email"
              label="Email"
              placeholder="Email"
            />

            <InputField
              required
              type="password"
              name="password"
              label="Password"
              placeholder="Password"
            />
          </FlexLayout>

          <Styles.CheckboxContainer>
            <Checkbox
              label="Remember me"
              checked={isRememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />

            <Link to={Routes.Auth.ForgotPassword}>Forgot password</Link>
          </Styles.CheckboxContainer>

          <Styles.ButtonsContainer>
            <StandardButton
              fullWidth
              text="Login"
              type={ButtonTypes.Submit}
              disabled={!dirty || isSubmitting}
            />
          </Styles.ButtonsContainer>
        </Styles.FormContainer>
      )}
    </AuthLayout>
  );
};

export default LoginPage;
